import React, { useEffect, useState, useRef } from "react";
import { Button, Box, Snackbar, Alert } from "@mui/material";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { Lock, Loader2, CheckCircle } from "lucide-react";

interface SaveTenderSubmitViewProps {
  isLoading: boolean;
  onClick: () => void;
  disabled?: boolean;
}

const CloseOfferSubmitView: React.FC<SaveTenderSubmitViewProps> = ({
  isLoading,
  disabled,
  onClick,
}) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const prevIsLoadingRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (prevIsLoadingRef.current && !isLoading) {
      setShowSnackbar(true);
    }
    prevIsLoadingRef.current = isLoading;
  }, [isLoading]);

  const handleCloseSnackbar = () => {
    setShowSnackbar(false);
  };

  const handleClick = () => {
    if (!isLoading && !disabled) {
      onClick();
    }
  };

  const getButtonContent = () => {
    if (isLoading) {
      return {
        icon: <Loader2 size={16} className="animate-spin" />,
        text: t("actions.processingCloseOffer"),
        color: "primary" as const,
      };
    } else if (disabled) {
      return {
        icon: <CheckCircle size={16} />,
        text: t("status.finished"),
        color: "success" as const,
      };
    } else {
      return {
        icon: <Lock size={16} />,
        text: t("actions.closeRequestForEvaluation"),
        color: "warning" as const,
      };
    }
  };

  const buttonContent = getButtonContent();

  return (
    <Box>
      <Button
        variant="contained"
        className="!bg-zinc-900"
        disabled={isLoading || disabled}
        onClick={handleClick}
        startIcon={buttonContent.icon}
        sx={{
          borderRadius: 2,
          px: 3,
          py: 1.5,
          textTransform: "none",
          fontWeight: 500,
          "&.Mui-disabled": {
            backgroundColor: "#f3f4f6",
            color: "#9ca3af",
          },
        }}
      >
        {buttonContent.text}
      </Button>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{
            width: "100%",
            borderRadius: 2,
          }}
        >
          {t("alerts.success.offerClosed")}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CloseOfferSubmitView;
